import React from 'react';
import { t } from 'i18next';
import classNames from 'classnames';
import { DobIcon } from '@dedalusdiit/dob-components';
import Styles from './ReportStatusCell.module.scss';
import { getReportStatusById, ReportStatus } from '@/Pages/GenericWorklist/Components/DataGrid/Columns/Components/ReportStatusCell/ReportStatusCellHelper';
import { AlignedReportValue } from '@/Pages/GenericWorklist/Logic/WorklistReportAlignmentHelper';
import GridStyles from '@/Pages/GenericWorklist/Components/DataGrid/WorklistDataGrid.module.scss';

export interface ReportStatusRowValue {
    status: number | null;
    isOrderTypeReport: boolean;
    isLocked: boolean;
    lockedBy: string | null;
}

interface ReportStatusCellProps {
    statusRows: readonly AlignedReportValue<ReportStatusRowValue>[];
    onClick?: (statusIndex: number) => void;
}

const getReportClassName = (reportStatus: ReportStatus | null): string =>
    reportStatus
        ? classNames(Styles.report, Styles[reportStatus.styleKey])
        : Styles.placeholder;

const getLockText = (value: ReportStatusRowValue): string =>
    value.lockedBy ? `${t('Locked by')} ${value.lockedBy}` : t('Locked');

const getReportTitle = (
    reportStatus: ReportStatus | null | undefined,
    value: ReportStatusRowValue,
): string | undefined => {
    if (!reportStatus && !value.isLocked) {
        return undefined;
    }

    const lockText = getLockText(value);
    if (!reportStatus) {
        return lockText;
    }

    return t(reportStatus.textKey) + (value.isLocked ? ` - ${lockText}` : '');
};

export function ReportStatusCell({ statusRows, onClick }: Readonly<ReportStatusCellProps>): React.ReactElement | null {
    if (statusRows.length === 0) {
        return null;
    }

    const orderTypeRow = statusRows.find((row) => row.value.isOrderTypeReport);
    if (orderTypeRow) {
        const orderTypeReportStatus = getReportStatusById(orderTypeRow.value.status);
        const reportTitle = getReportTitle(orderTypeReportStatus, orderTypeRow.value);

        return (
            <div className={Styles.orderTypeCell}>
                <span className={Styles.reportWrapper} title={reportTitle}>
                    <span className={getReportClassName(orderTypeReportStatus)} />
                    {orderTypeRow.value.isLocked && (
                        <span className={Styles.lockIcon} title={reportTitle}>
                            <DobIcon name="lock-fill" size="10px" />
                        </span>
                    )}
                </span>
            </div>
        );
    }

    return (
        <div className={GridStyles['order-procedure-cell']}>
            {statusRows.map(({ key, value }, index) => {
                const reportStatus = getReportStatusById(value.status);
                const reportTitle = getReportTitle(reportStatus, value);
                if (!reportStatus) {
                    return (
                        <div key={key} className={GridStyles['order-procedure-cell_content']}>
                            <span className={Styles.reportWrapper} title={reportTitle}>
                                <span className={getReportClassName(reportStatus)} />
                                {value.isLocked && (
                                    <span className={Styles.lockIcon} title={reportTitle}>
                                        <DobIcon name="lock-fill" size="10px" />
                                    </span>
                                )}
                            </span>
                        </div>
                    );
                }

                return (
                    <div key={key} className={GridStyles['order-procedure-cell_content']}>
                        <button
                            type="button"
                            className={classNames(Styles.reportWrapper, { [Styles.clickable]: !!onClick })}
                            onClick={onClick
                                ? (event) => {
                                        event.stopPropagation();
                                        onClick(index);
                                    }
                                : undefined}
                            title={reportTitle}
                        >
                            <span className={getReportClassName(reportStatus)} />
                            {value.isLocked && (
                                <span className={Styles.lockIcon} title={reportTitle}>
                                    <DobIcon name="lock-fill" size="10px" />
                                </span>
                            )}
                        </button>
                    </div>
                );
            })}
        </div>
    );
}
